'use client';

import clsx from 'clsx';
import AutoHeight from 'embla-carousel-auto-height';
import Fade from 'embla-carousel-fade';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import React, { FC, useEffect, useState } from 'react';
import BackgroundImage from '~/assets/images/background.jpg';
import Button from '~/components/button';
import EmblaSlider from '~/components/embla-slider';
import EmblaContainer from '~/components/embla-slider/container';
import SliderNavigation from '~/components/embla-slider/navigation';
import EmblaSlide from '~/components/embla-slider/slide';
import Heading from '~/components/heading';
import IBlockSlider from '~/data/strapi/types/single-types/main-page/block-slider';
import alt from '~/utils/default-alt';
import getDataPath from '~/utils/get-data-path';
import sanitizeHtml from '~/utils/sanitize-html';

import SlidePattern from './pattern';
import styles from './styles.module.css';

interface Props {
  data: IBlockSlider;
}

const BlockSlider: FC<Props> = ({ data }) => {
  const [ref, carousel] = useEmblaCarousel({ loop: true }, [Fade(), AutoHeight()]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [init, setInit] = useState<boolean>(false);

  const slides = data.slides;

  useEffect(() => {
    carousel?.on('select', (api) => setActiveIndex(api.selectedScrollSnap()));
  }, [carousel]);

  useEffect(() => {
    if (!carousel) return;
    setInit(true);
  }, [carousel]);

  const nextSlide = () => {
    carousel?.scrollNext();
  };

  const prevSlide = () => {
    carousel?.scrollPrev();
  };

  if (!slides) return null;

  return (
    <article>
      <div className={styles.backgroundImage}>
        <Image src={BackgroundImage} alt="" fill sizes="100vw" unoptimized />
      </div>
      <EmblaSlider ref={ref} className={clsx(styles.slider, init && styles.initialized)}>
        <EmblaContainer className={styles.sliderContainer}>
          {slides.map((slide) => (
            <EmblaSlide className={styles.slide} key={slide.id}>
              <div className={styles.leftPart}>
                <div className={styles.titleContainer}>
                  {slide.heading && <Heading data={slide.heading} className={styles.title} />}
                  {slide.description && (
                    <div
                      className={styles.description}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(slide.description) }}
                    />
                  )}
                </div>

                {slide.buttons && (
                  <div className={styles.buttonContainer}>
                    {slide.buttons.map((button, index) => (
                      <Button data={button} className={styles.btn} key={index} />
                    ))}
                  </div>
                )}
              </div>

              {slide.image && (
                <div className={styles.rightPart}>
                  {
                    <Image
                      src={getDataPath(slide.image.url)}
                      priority
                      alt={alt(slide.image.name)}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                    />
                  }

                  <SliderNavigation onNext={nextSlide} onPrev={prevSlide} containerClassName={styles.navigation} />
                </div>
              )}

              <SlidePattern activeIndex={activeIndex} />
            </EmblaSlide>
          ))}
        </EmblaContainer>
      </EmblaSlider>
    </article>
  );
};

export default BlockSlider;
